import { Timeline } from "antd";
import type { DeliveryVo } from "@zhihaoscm/entity";
import { SHIP_WAY_BILL_TYPE_PC, THEME_MODE, TRANSPORT_ORDER_SHIP_STATE } from "@zhihaoscm/meta";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useEffect, useState } from "react";
import { deviceService } from "@/api/deviceService";
import { shipService } from "@/api/shipService";
import { EmptyState } from "@/components/common";
import { ZhihaoMap } from "@/components/media";

interface LogisticsActivity {
    timestamp: string;
    tip: string;
    value: number;
}

type ShipDevice = NonNullable<Awaited<ReturnType<typeof shipService.getInfo>>["devices"]>[number];

/** 服务运单节点，与 Vue supervisionActivities 保持相同顺序和文案。 */
const supervisionActivities: LogisticsActivity[] = [
    { timestamp: TRANSPORT_ORDER_SHIP_STATE.AWAITING_DEPARTURE.intro, tip: "船舶处于装货阶段，准备发航", value: TRANSPORT_ORDER_SHIP_STATE.AWAITING_DEPARTURE.code },
    { timestamp: TRANSPORT_ORDER_SHIP_STATE.DURING_TRANSPORTATION.intro, tip: "船舶已发航，正在运输中", value: TRANSPORT_ORDER_SHIP_STATE.DURING_TRANSPORTATION.code },
    { timestamp: TRANSPORT_ORDER_SHIP_STATE.TO_BE_UNLOADED.intro, tip: "船舶已到港，准备卸货", value: TRANSPORT_ORDER_SHIP_STATE.TO_BE_UNLOADED.code },
    { timestamp: TRANSPORT_ORDER_SHIP_STATE.COMPLETED.intro, tip: "已卸货清仓完毕，船运单已完成", value: TRANSPORT_ORDER_SHIP_STATE.COMPLETED.code }
];

/** 撮合运单节点，与 Vue matchingActivities 保持相同顺序和文案。 */
const matchingActivities: LogisticsActivity[] = [
    { timestamp: TRANSPORT_ORDER_SHIP_STATE.UNCOMPLETED.intro, tip: "已开始船运单履约流程", value: TRANSPORT_ORDER_SHIP_STATE.UNCOMPLETED.code },
    { timestamp: TRANSPORT_ORDER_SHIP_STATE.COMPLETED.intro, tip: "船运单已完成", value: TRANSPORT_ORDER_SHIP_STATE.COMPLETED.code }
];

/** 逐项迁移自旧 delivery/components/logisticsTrack.vue。 */
export function DeliveryLogisticsTracking({ data, embedded = false }: { data: DeliveryVo; embedded?: boolean }) {
    const [ships, setShips] = useState<Awaited<ReturnType<typeof shipService.findCreate>>[]>([]);
    const [devices, setDevices] = useState<ShipDevice[]>([]);
    const [capture, setCapture] = useState("");
    const [track, setTrack] = useState<unknown[]>([]);
    const [displayMode, setDisplayMode] = useState<"ais" | "device">("ais");
    const [videoUrl, setVideoUrl] = useState("");
    const shipId = data.delivery.shipId;
    const transport = data.transportOrderShip;
    const transportState = Number(transport?.state);
    const timelineItems = useMemo(() => {
        if (!transport) return [];
        const activities = Number(transport.serviceType) === SHIP_WAY_BILL_TYPE_PC.SUPERVISION.code
            ? supervisionActivities
            : matchingActivities;
        const currentIndex = activities.findIndex(activity => activity.value === transportState);
        // Vue 只显示截至当前状态的节点，并将当前节点置顶。
        return activities.slice(0, currentIndex + 1).reverse().map(activity => ({
            color: activity.value === transportState ? "#1777ff" : "#d9d9d9",
            children: (
                <div className={activity.value === transportState ? "active" : "not-active"}>
                    [{activity.timestamp}] <span>{activity.tip}</span>
                </div>
            )
        }));
    }, [transport, transportState]);
    useEffect(() => {
        if (!shipId) { setShips([]); setDevices([]); setCapture(""); setTrack([]); return; }
        void shipService.findCreate(shipId).then(ship => setShips(ship ? [ship] : [])).catch(() => setShips([]));
        void shipService.getInfo(Number(shipId)).then(async ship => {
            const nextDevices = ship.devices ?? [];
            setDevices(nextDevices);
            if (nextDevices[0]?.serialNo) setCapture(await deviceService.getCapture(nextDevices[0].serialNo));
        }).catch(() => { setDevices([]); setCapture(""); });
    }, [shipId]);
    useEffect(() => {
        const transportDetail = data.transportOrderDetailsShip;
        const start = transportDetail?.deliveryInfo?.loadingDate
            || transportDetail?.lazbInfo?.loadingCompletionTime
            || transportDetail?.ownerSetSailTime;
        if (!shipId || !start) { setTrack([]); return; }
        const end = transportDetail?.captainArrivalTime || new Date();
        // Vue mapVideoWrap 以装货、装载完成、发航时间依次回退作为轨迹起点。
        void shipService.aisTrack({
            mmsi: shipId,
            start: dayjs(start).format("YYYY-MM-DD HH:mm"),
            end: dayjs(end).format("YYYY-MM-DD HH:mm")
        }).then(points => setTrack(points ?? [])).catch(() => setTrack([]));
    }, [data.transportOrderDetailsShip, shipId]);

    /** 点击抓拍图后与 Vue 一样切换到设备预览，再点击画面开始取流。 */
    const openVideo = async () => {
        const serialNo = devices[0]?.serialNo;
        if (!serialNo) return;
        setVideoUrl(await deviceService.videoPlay(serialNo, THEME_MODE.PC_LIVE.code));
    };
    return (
        <section className={`section-card delivery-detail-section${embedded ? " delivery-detail-section--embedded" : ""}`} id="delivery-logistics">
            {embedded ? null : <h2 className="section-title">物流跟踪</h2>}
            <div className="delivery-logistics">
                {transport ? <Timeline className="delivery-logistics-timeline" items={timelineItems} /> : <div className="delivery-logistics-empty"><EmptyState description="暂未关联运单数据" /></div>}
                <div className="delivery-logistics-map">
                    {displayMode === "ais" ? (
                        <>
                            <ZhihaoMap label="船舶轨迹" layerType="satellite" ships={ships} track={track.length && shipId ? { id: shipId, points: track, color: "#ff0000" } : undefined} />
                            {devices.length ? <button aria-label="切换船舶监控" className="delivery-video-preview" onClick={() => setDisplayMode("device")} type="button">{capture ? <img alt="船舶监控" src={capture} /> : null}<span /></button> : null}
                        </>
                    ) : (
                        <>
                            <div className="delivery-video-main">
                                {videoUrl ? <iframe allowFullScreen src={videoUrl} title="船舶监控" /> : <button aria-label="播放船舶监控" onClick={() => void openVideo()} type="button">{capture ? <img alt="船舶监控" src={capture} /> : null}<div className="delivery-video-name">设备名称：{devices[0]?.name || "--"} | {devices[0]?.position || "--"}</div><i className="iconfont icon-Frame1" /></button>}
                            </div>
                            <div aria-label="切换船舶轨迹" className="delivery-map-preview" onClick={() => setDisplayMode("ais")} onKeyDown={event => { if (event.key === "Enter" || event.key === " ") setDisplayMode("ais"); }} role="button" tabIndex={0}><ZhihaoMap label="船舶轨迹缩略图" layerType="satellite" ships={ships} /></div>
                        </>
                    )}
                </div>
            </div>
        </section>
    );
}
